import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { moveArrayItemToNewIndex } from '../../../lib/js/Array';
import { focusBlockHeadlineById } from './ContentWrapper.utils';
import { GlobalDocumentIds } from '../../../types/GlobalDocumentIds';
import { sendLiveRegionMessage } from '../../atoms/LiveRegionGlobal/LiveRegionGlobal';
import { Namespace } from '../../../translation/namespace';
import { isNotNullOrUndefined } from '../../../lib/typeGuards';
import { ContentWrapperAction, ContentWrapperBlocks, ContentWrapperError } from './ContentWrapper.types';
import { BlockType } from '../../../types/api/ElementType';
import { DefaultView, OptionalEditView } from '../../../types/BlockWithViews';
// eslint-disable-next-line max-len
import { CollapsibleContainerBlockDefaultView } from '../../../_blocks/CollapsibleContainerBlock/views/CollapsibleContainerBlockDefaultView';
// eslint-disable-next-line max-len
import { CollapsibleContainerBlockEditView } from '../../../_blocks/CollapsibleContainerBlock/views/CollapsibleContainerBlockEditView';

/** ErrorState variable in ContentWrapper
 *
 * holds a type and a uuid which the corresponding block
 * can match on its own uuid.
 */
export type ErrorState = {
  /** uuid of the block being moved, deleted,... Is undefined when created */
  uuid?: string;
  type?: keyof typeof ContentWrapperError;
};

/** ActionState variable in ContentWrapper
 *
 * holds a type (`ContentWrapperAction`) and a uuid
 * which the corresponding block can match on its own uuid.
 */
export type ActionState = {
  /** uuid of the block being moved, deleted,... Is undefined when created */
  uuid?: string;
  type?: keyof typeof ContentWrapperAction;
};

/** Responsible for moving blocks
 *
 * Responsible for
 * - adding blocks (and focus)
 * - moving blocks (and focus)
 *   - injecting actions into the blocks
 * - removing blocks
 *   - set focus on previous block
 * - connecting blocks
 */
export function useContentWrapper(
  initialBlocks: ContentWrapperBlocks[],
  fetchUrl: string,
  collapsibleParentUuid?: string,
  moveOutsideOfCollapsibleBlock?: (parentUuid: string | undefined, block: ContentWrapperBlocks, actionKey: string)
  => void,
  registerStateHandler?: (uuid: string, callback: (block: ContentWrapperBlocks, actionKey: string) => void) => void,
  onCollapsibleBlockContentWrapperChange?: (
    collapsibleBlockUuid: string,
    collapsibleContentNumberOfBlocks: number,
  ) => void,
) {
  const { t } = useTranslation(Namespace.ContentWrapper);
  const [blocks, setBlocks] = useState<ContentWrapperBlocks[]>(() => initialBlocks);
  const [action, setAction] = useState<ActionState | null>(null);
  const [error, setError] = useState<ErrorState | null>(null);
  const [blockIdToFocus, setBlockIdToFocus] = useState<string>();
  const [collapsibleBlocksNumberOfBlocks, setCollapsibleBlocksNumberOfBlocks] = useState<Map<string, number>>(
    initialBlocks.filter(b => b.elementType === BlockType.collapsiblecontainer)
      .reduce((acc, block: any) => {
        acc.set(block.uuid, block.embedded?.views?.default?.embedded?.content.length);

        return acc;
      }, new Map<string, number>()),
  );
  const collapsibleStateHandlerCallback = useRef(
    new Map<string, (block: ContentWrapperBlocks, actionKey: string) => void>());

  // snapshot of block order before user started moving them via drag and drop
  // -> our way of dnd is by changing the state on every change, not by defining different drop regions.
  // -> this is why we need one snapshot to reset to when something goes wrong.
  const blockSnapShot = useRef<ContentWrapperBlocks[]>(null);

  const collapsibleBlocksNumberOfBlocksChanged = (
    collapsibleBlockUuid: string, collapsibleContentNumberOfBlocks: number) => {
    const m = new Map<string, number>();

    collapsibleBlocksNumberOfBlocks.entries().forEach(([uuid, numberOfBlocks]) => {
      m.set(uuid, numberOfBlocks);
    });

    m.set(collapsibleBlockUuid, collapsibleContentNumberOfBlocks);

    setCollapsibleBlocksNumberOfBlocks(m);
  };

  const createSnapshotOfBlockOrder = () => {
    blockSnapShot.current = blocks;
  };

  const moveBlockToBlock = (sourceId: string, targetId: string) => {
    const sourceIdIndex = blocks.findIndex(b => b.uuid === sourceId);
    const targetIdIndex = blocks.findIndex(b => b.uuid === targetId);
    const newBlocks = moveArrayItemToNewIndex(blocks, sourceIdIndex, targetIdIndex).filter(isNotNullOrUndefined);
    setBlocks(newBlocks);
  };

  const handleMoveBlockOutFromCollapsibleBlock = (
    parentUuid: string | undefined,
    block: ContentWrapperBlocks,
    actionKey: string,
  ) => {
    if (parentUuid) {
      const blockIndex = blocks.findIndex(b => b.uuid === parentUuid);

      if (blockIndex !== -1) {
        if (actionKey === ContentWrapperAction.moveBlockUp) {
          setBlocks(blocks.toSpliced(blockIndex, 0, block));
        }

        if (actionKey === ContentWrapperAction.moveBlockDown) {
          setBlocks(blocks.toSpliced(blockIndex + 1, 0, block));
        }

        setAction(null);
        setError(null);
        const blockToFocus = blocks.at(blockIndex);

        if (blockToFocus) {
          setBlockIdToFocus(`${blockToFocus.elementType}-${blockToFocus.uuid}`);
        }

        if (typeof onCollapsibleBlockContentWrapperChange === 'function' && collapsibleParentUuid) {
          onCollapsibleBlockContentWrapperChange(collapsibleParentUuid, blocks.length - 1);
        }
      }
    }
  };

  const registerCollapsibleStateHandlerCallback = (
    uuid: string, callback: (block: ContentWrapperBlocks, actionKey: string) => void) => {
    collapsibleStateHandlerCallback.current.set(uuid, callback);
  };

  const moveBlockIntoCollapsibleBlock = useCallback((
    block: ContentWrapperBlocks,
    actionKey: string,
  ) => {
    let newBlocks;

    if (blocks) {
      newBlocks = actionKey === ContentWrapperAction.moveBlockUp
        ? [...blocks, block]
        : [block, ...blocks];
    } else {
      newBlocks = [block];
    }

    if (typeof onCollapsibleBlockContentWrapperChange === 'function' && collapsibleParentUuid) {
      onCollapsibleBlockContentWrapperChange(collapsibleParentUuid, newBlocks.length);
    }

    setBlocks(newBlocks);
    setAction(null);
    setError(null);

    if (block) {
      setBlockIdToFocus(`${block.elementType}-${block.uuid}`);
    }
  }, [blocks, collapsibleParentUuid, onCollapsibleBlockContentWrapperChange]);

  useEffect(() => {
    if (typeof registerStateHandler === 'function' && collapsibleParentUuid) {
      registerStateHandler(collapsibleParentUuid, moveBlockIntoCollapsibleBlock);
    }
  }, [collapsibleParentUuid, moveBlockIntoCollapsibleBlock, registerStateHandler]);

  const submitNewBlockToBlockMove = (blockUuid: string) => {
    const movedBlock = blocks.find(block => block.uuid === blockUuid);
    // @ts-expect-error move block url is here !
    const moveBlockUrl = movedBlock?.actions?.moveBlockDown?.url || movedBlock.actions?.moveBlockUp?.url;
    const oldPosition = blockSnapShot.current?.findIndex(b => b.uuid === blockUuid);
    const newPosition = blocks.findIndex(b => b.uuid === blockUuid);

    if (moveBlockUrl && oldPosition !== undefined && oldPosition !== newPosition) {
      const formdata = new FormData();
      formdata.set('step', `${newPosition - oldPosition}`);
      setError(null);
      axios
        .post(moveBlockUrl, formdata)
        .then(() => {
          // focus block headline on drop
          if (movedBlock) {
            setBlockIdToFocus(`${movedBlock.elementType}-${movedBlock.uuid}`);
          }
        })
        .catch(() => {
          setError({ uuid: blockUuid, type: ContentWrapperError.moveFail });
          if (blockSnapShot.current) {
            setBlocks(blockSnapShot.current);
          }
        });
    }
  };

  const addBlock = useCallback((newBlock: ContentWrapperBlocks, position: number) => {
    const newListOfBlocks = blocks.toSpliced(position, 0, newBlock);
    setBlocks(newListOfBlocks);

    if (typeof onCollapsibleBlockContentWrapperChange === 'function' && collapsibleParentUuid) {
      onCollapsibleBlockContentWrapperChange(collapsibleParentUuid, newListOfBlocks.length);
    }

    if (newBlock) {
      setBlockIdToFocus(`${newBlock.elementType}-${newBlock.uuid}`);
    }
  }, [blocks, collapsibleParentUuid, onCollapsibleBlockContentWrapperChange]);

  const onMovingFailure = (uuid: string) => {
    setAction(null);
    setError({ uuid, type: ContentWrapperError.moveFail });
  };

  const onMovingSuccess = (actionKey: string, uuid: string, elementType: string) => {
    const blockIndex = blocks.findIndex(b => b.uuid === uuid);

    if (blockIndex !== -1) {
      const newBlockIndex = actionKey === ContentWrapperAction.moveBlockUp ? blockIndex - 1 : blockIndex + 1;

      if (collapsibleParentUuid) {
        if (newBlockIndex === -1 || newBlockIndex === blocks.length) {
          setBlocks(blocks.filter(b => b.uuid !== uuid));

          if (moveOutsideOfCollapsibleBlock) {
            moveOutsideOfCollapsibleBlock(collapsibleParentUuid, blocks[blockIndex], actionKey);
          }

          setAction(null);
          setError(null);
          const blockToFocus = blocks.at(blockIndex);

          if (blockToFocus) {
            setBlockIdToFocus(`${blockToFocus.elementType}-${blockToFocus.uuid}`);
          }
        }
      }

      if (blocks[newBlockIndex].elementType === BlockType.collapsiblecontainer &&
        blocks[blockIndex].elementType !== BlockType.collapsiblecontainer
      ) {
        const blockToMove = blocks.at(blockIndex);
        const blockToMoveInto = blocks[newBlockIndex] as ContentWrapperBlocks & {
          embedded: {
            views: DefaultView<CollapsibleContainerBlockDefaultView> &
              OptionalEditView<CollapsibleContainerBlockEditView>;
          };
        };

        if (blockToMove) {
          const callback = collapsibleStateHandlerCallback.current?.get(blockToMoveInto.uuid);
          if (callback) {
            callback(blockToMove, actionKey);
          }

          setBlocks(blocks.filter(b => b.uuid !== uuid));
          setAction(null);
          setError(null);
          setBlockIdToFocus(`${blockToMove.elementType}-${blockToMove.uuid}`);
        }
      } else {
        const visualIndex = newBlockIndex + 1;
        sendLiveRegionMessage(t(`moveSuccess.${elementType}`, { position: visualIndex }));

        const newBlocks = moveArrayItemToNewIndex(blocks, blockIndex, newBlockIndex).filter(isNotNullOrUndefined);
        setBlocks(newBlocks);
        setAction(null);
        setError(null);

        const blockToFocus = newBlocks.at(newBlockIndex);

        if (blockToFocus) {
          setBlockIdToFocus(`${blockToFocus.elementType}-${blockToFocus.uuid}`);
        }
      }
    }
  };

  /**
   * When a block is deleted successfully in the backend the following happens:
   * - the block is deleted in the frontend
   * - the previous block will be focused
   * - if there is no previous block, the addContentBlock will be focused
   */
  const onDeleteSuccess = (uuid: string) => {
    const newListOfBlocks = blocks.filter(block => block.uuid !== uuid);

    setBlocks(newListOfBlocks);

    if (typeof onCollapsibleBlockContentWrapperChange === 'function' && collapsibleParentUuid) {
      onCollapsibleBlockContentWrapperChange(collapsibleParentUuid, newListOfBlocks.length);
    }

    const deletedBlockIndex = blocks.findIndex(b => b.uuid === uuid);
    const blockToFocus = deletedBlockIndex - 1 > -1
      ? blocks.at(deletedBlockIndex - 1)
      : blocks.at(deletedBlockIndex + 1);

    if (blockToFocus) {
      setBlockIdToFocus(`${blockToFocus.elementType}-${blockToFocus.uuid}`);
    } else {
      setBlockIdToFocus(GlobalDocumentIds.addContentBlock);
    }
  };

  const onConnectSuccess = (uuid: string, resp: any) => {
    const index = blocks.findIndex(block => block.uuid === uuid);
    let blockToFocus = undefined;

    if (index !== -1) {
      if (Array.isArray(resp.data)) {
        // we have dissolved a LayoutGroup
        setBlocks(blocks.toSpliced(index, 1, ...resp.data));
        blockToFocus = resp.data.at(0);
      } else {
        // we have created a new LayoutGroup
        setBlocks(blocks.toSpliced(index, 2, resp.data));
        blockToFocus = resp.data;
      }

      if (blockToFocus) {
        setBlockIdToFocus(`${blockToFocus.elementType}-${blockToFocus.uuid}`);
      }
    }
  };

  /**
     * refetches the ContentWrappers.
     * Takes an uuid so that the corresponding block can display the Error
     */
  const refetch = async (uuid: string) => {
    const res = await axios.get(fetchUrl);
    if (res.status >= 200 && res.status < 300) {
      if (res.data?.views?.default?.embedded?.content) {
        setBlocks(res.data.views.default.embedded.content);
      }
    } else {
      setError({ uuid, type: ContentWrapperError.fetchError });
    }
  };

  useEffect(() => {
    setBlocks(initialBlocks);
  }, [initialBlocks]);

  /**
   * Handle focus when
   * - block was added -> focus added block
   * - block is removed -> focus prev block
   * - layoutgroup is created -> focus on layoutgroup
   * - layoutgroup got disassembled -> focus first child block
   */
  useEffect(() => {
    if (blockIdToFocus) {
      focusBlockHeadlineById(blockIdToFocus);
      setBlockIdToFocus(undefined);
    }
  }, [blockIdToFocus, blocks]);

  return {
    blocks,
    addBlock,
    action,
    setAction,
    error,
    setError,
    refetch,
    onMovingSuccess,
    onMovingFailure,
    onDeleteSuccess,
    submitNewBlockToBlockMove,
    moveBlockToBlock,
    createSnapshotOfBlockOrder,
    handleMoveBlockOutFromCollapsibleBlock,
    registerCollapsibleStateHandlerCallback,
    onConnectSuccess,
    collapsibleBlocksNumberOfBlocks,
    collapsibleBlocksNumberOfBlocksChanged,
  };
}
